import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import css from './SchoolAdminDashboard.module.css';

const TERM_OPTIONS = [
  { value: 'first', label: 'First Term' },
  { value: 'second', label: 'Second Term' },
  { value: 'third', label: 'Third Term' },
];

const MAX_GROUPS = 10;

const subtle = { color: '#607d8b', fontSize: '0.82rem', lineHeight: 1.5 };
const stepBox = { background: '#f8fbfc', border: '1px solid #e0e8ea', borderRadius: 10, padding: '12px 14px', marginBottom: 16 };
const groupBox = { border: '1px solid #e0e8ea', borderRadius: 12, padding: 14, marginBottom: 12, background: '#fff' };
const sectionTitle = { fontSize: '0.95rem', fontWeight: 700, color: '#0C1E28', margin: '20px 0 6px' };
const filterRow = { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 16 };
const filterField = { flex: '1 1 180px', minWidth: 0, marginBottom: 0 };
const actionRow = { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' };
const smallBtn = { padding: '5px 10px', fontSize: '0.78rem' };

let tempKey = 0;
const newGroup = () => ({ key: `new-${++tempKey}`, _id: null, name: '', description: '', subjectIds: [], choose: 1 });

function groupsFromPolicy(policy) {
  return (policy?.electiveGroups || []).map(group => ({
    key: group._id,
    _id: group._id,
    name: group.name,
    description: group.description || '',
    subjectIds: [...group.subjectIds],
    choose: group.choose,
  }));
}

function sameGroups(a, b) {
  const norm = groups => JSON.stringify(groups.map(g => ({
    _id: g._id || null, name: g.name.trim(), description: (g.description || '').trim(), choose: Number(g.choose), subjectIds: [...g.subjectIds].sort(),
  })));
  return norm(a) === norm(b);
}

function localGroupErrors(groups) {
  const errors = [];
  const used = new Map();
  const names = new Set();
  groups.forEach((g, i) => {
    const label = g.name.trim() || `Group ${i + 1}`;
    if (!g.name.trim()) errors.push(`Group ${i + 1}: give the group a name (e.g. "Religious Studies").`);
    else if (names.has(g.name.trim().toLowerCase())) errors.push(`${label}: two groups have the same name.`);
    names.add(g.name.trim().toLowerCase());
    if (g.subjectIds.length < 2) errors.push(`${label}: tick at least two subjects students can choose between.`);
    g.subjectIds.forEach(id => {
      if (used.has(id)) errors.push(`${label}: a subject is already in "${used.get(id)}". Each subject can only be in one group.`);
      used.set(id, label);
    });
    if (g.subjectIds.length >= 2 && Number(g.choose) >= g.subjectIds.length) {
      errors.push(`${label}: students must choose fewer subjects than the group has.`);
    }
  });
  return [...new Set(errors)];
}

function selectionMap(selections) {
  const map = {};
  (selections || []).forEach(sel => { map[sel.groupId] = [...sel.subjectIds]; });
  return map;
}

function isComplete(groups, map) {
  return groups.every(g => (map[g._id] || []).length === g.choose);
}

export default function SubjectChoicesSegment({ classes = [], schoolFetch }) {
  const [classId, setClassId] = useState('');
  const [term, setTerm] = useState('');
  const [session, setSession] = useState('');
  const [sessionDraft, setSessionDraft] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [groups, setGroups] = useState([]);
  const [choices, setChoices] = useState({});
  const [dirty, setDirty] = useState(new Set());
  const [busy, setBusy] = useState('');
  const [search, setSearch] = useState('');
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [copySource, setCopySource] = useState('');

  const subjectName = useMemo(() => {
    const map = {};
    (data?.subjects || []).forEach(s => { map[s._id] = s.name; });
    return map;
  }, [data]);

  // Only the newest load may update the screen, so a slow response for a class
  // the admin already left can never replace the class they are now editing.
  const loadSeq = useRef(0);
  const load = useCallback(async (next = {}) => {
    const cid = next.classId ?? classId;
    if (!cid) return;
    const seq = ++loadSeq.current;
    const params = new URLSearchParams({ classId: cid });
    const t = next.term ?? term;
    const s = next.session ?? session;
    if (t) params.set('term', t);
    if (s) params.set('session', s);
    setLoading(true);
    setError('');
    try {
      const res = await schoolFetch(`/school/subject-choices?${params.toString()}`);
      if (seq !== loadSeq.current) return;
      if (!res.ok) {
        setData(null);
        setError(res.data?.error || 'Could not load subject choices.');
        return;
      }
      setData(res.data);
      setTerm(res.data.term);
      setSession(res.data.session);
      setSessionDraft(res.data.session);
      setGroups(groupsFromPolicy(res.data.policy));
      const map = {};
      (res.data.students || []).forEach(st => { map[st.membershipId] = selectionMap(st.selections); });
      setChoices(map);
      setDirty(new Set());
      setCopySource('');
    } catch {
      if (seq === loadSeq.current) setError('Could not load subject choices. Check your connection and try again.');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [classId, term, session, schoolFetch]);

  useEffect(() => {
    if (!classId && classes.length > 0) setClassId(classes[0]._id);
  }, [classes, classId]);

  useEffect(() => {
    if (classId) load({ classId });
    // Reload only when the class changes; term/session changes call load directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  const savedGroups = useMemo(() => groupsFromPolicy(data?.policy), [data]);
  const groupsChanged = !sameGroups(groups, savedGroups);
  const policy = data?.policy || null;
  const isActive = policy?.status === 'active';
  const hasUnsavedChoices = dirty.size > 0;
  const groupErrors = useMemo(() => localGroupErrors(groups), [groups]);
  const classSubjects = data?.subjects || [];
  const usedElsewhere = useCallback((groupKey) => {
    const used = new Map();
    groups.forEach(g => { if (g.key !== groupKey) g.subjectIds.forEach(id => used.set(id, g.name.trim() || 'another group')); });
    return used;
  }, [groups]);

  // Saves always target the class/term/session currently displayed.
  const shownContext = () => ({ classId: data?.classInfo?._id || classId, term: data?.term || term, session: data?.session || session });

  const confirmDiscard = () => (!groupsChanged && !hasUnsavedChoices)
    || window.confirm('You have unsaved changes. Leave without saving them?');

  const changeClass = (value) => {
    if (!confirmDiscard()) return;
    setSuccess('');
    setClassId(value);
  };
  const changeTerm = (value) => {
    if (!confirmDiscard()) return;
    setSuccess('');
    setTerm(value);
    load({ term: value });
  };
  const applySession = () => {
    const value = sessionDraft.trim();
    if (!value || value === session) { setSessionDraft(session); return; }
    if (!confirmDiscard()) { setSessionDraft(session); return; }
    setSuccess('');
    setSession(value);
    load({ session: value });
  };

  const updateGroup = (key, patch) => setGroups(prev => prev.map(g => (g.key === key ? { ...g, ...patch } : g)));
  const toggleGroupSubject = (key, subjectId) => setGroups(prev => prev.map(g => {
    if (g.key !== key) return g;
    const subjectIds = g.subjectIds.includes(subjectId) ? g.subjectIds.filter(id => id !== subjectId) : [...g.subjectIds, subjectId];
    const choose = Math.min(Number(g.choose) || 1, Math.max(1, subjectIds.length - 1));
    return { ...g, subjectIds, choose };
  }));

  const handleResult = (res, fallback) => {
    if (res.status === 202 && res.data?.queued) {
      setSuccess(res.data.message || 'Sent for approval.');
      return false;
    }
    if (!res.ok) {
      setError(res.data?.error || fallback);
      return false;
    }
    return true;
  };

  const syncNote = (sync) => {
    if (!sync || (!sync.added && !sync.removed && !sync.restored)) return '';
    const parts = [];
    if (sync.restored) parts.push(`${sync.restored} open exam(s) given back to students who had been skipped because of their choices`);
    if (sync.added) parts.push(`${sync.added} open elective exam(s) added to students' lists`);
    if (sync.removed) parts.push(`${sync.removed} unstarted exam(s) removed for students who no longer take that subject`);
    return ` ${parts.join('; ')}.`;
  };

  const saveGroups = async () => {
    setError(''); setSuccess('');
    if (groupErrors.length) { setError(groupErrors[0]); return; }
    setBusy('groups');
    try {
      const res = await schoolFetch('/school/subject-choices/policy', {
        method: 'PUT',
        body: JSON.stringify({
          ...shownContext(),
          electiveGroups: groups.map(g => ({ _id: g._id || undefined, name: g.name.trim(), description: g.description.trim(), subjectIds: g.subjectIds, choose: Number(g.choose) })),
        }),
      });
      if (!handleResult(res, 'Could not save the groups.')) return;
      await load();
      setSuccess(`${res.data.message || 'Groups saved.'}${syncNote(res.data.sync)}`);
    } catch {
      setError('Could not save the groups. Check your connection and try again.');
    } finally { setBusy(''); }
  };

  const setStatus = async (status) => {
    setConfirm(null);
    setError(''); setSuccess('');
    setBusy('status');
    try {
      const res = await schoolFetch('/school/subject-choices/policy/status', {
        method: 'POST',
        body: JSON.stringify({ ...shownContext(), status }),
      });
      if (!handleResult(res, 'Could not update subject choices.')) return;
      await load();
      setSuccess(`${res.data.message}${syncNote(res.data.sync)}`);
    } catch {
      setError('Could not update subject choices. Check your connection and try again.');
    } finally { setBusy(''); }
  };

  const copyFrom = async () => {
    const source = (data?.copySources || [])[Number(copySource)];
    if (!source) return;
    if (!window.confirm(`Copy the groups and students' choices from ${source.label}? Choices already saved for this term are kept.`)) return;
    setError(''); setSuccess('');
    setBusy('copy');
    try {
      const res = await schoolFetch('/school/subject-choices/copy', {
        method: 'POST',
        body: JSON.stringify({ ...shownContext(), fromTerm: source.term, fromSession: source.session }),
      });
      if (!handleResult(res, 'Could not copy subject choices.')) return;
      await load();
      setSuccess(res.data.message);
    } catch {
      setError('Could not copy subject choices. Check your connection and try again.');
    } finally { setBusy(''); }
  };

  const setStudentChoice = (membershipId, group, subjectId, checked) => {
    setChoices(prev => {
      const current = prev[membershipId] || {};
      let list = current[group._id] || [];
      if (group.choose === 1) list = checked ? [subjectId] : [];
      else if (checked) list = list.includes(subjectId) ? list : [...list, subjectId];
      else list = list.filter(id => id !== subjectId);
      return { ...prev, [membershipId]: { ...current, [group._id]: list } };
    });
    setDirty(prev => new Set(prev).add(membershipId));
  };

  const fillBlanks = (group, subjectId) => {
    if (!subjectId || group.choose !== 1) return;
    const targets = (data?.students || []).filter(st => !(choices[st.membershipId]?.[group._id] || []).length);
    if (!targets.length) { setSuccess(`Every student already has a choice for ${group.name}.`); return; }
    setChoices(prev => {
      const next = { ...prev };
      targets.forEach(st => { next[st.membershipId] = { ...(next[st.membershipId] || {}), [group._id]: [subjectId] }; });
      return next;
    });
    setDirty(prev => { const next = new Set(prev); targets.forEach(st => next.add(st.membershipId)); return next; });
    setSuccess(`${subjectName[subjectId]} filled in for ${targets.length} student(s) without a choice. Review, then press "Save student choices".`);
  };

  const saveChoices = async () => {
    if (!dirty.size) return;
    setError(''); setSuccess('');
    setBusy('choices');
    try {
      const enrolments = [...dirty].map(membershipId => ({
        membershipId,
        selections: savedGroups
          .map(g => ({ groupId: g._id, subjectIds: choices[membershipId]?.[g._id] || [] }))
          .filter(sel => sel.subjectIds.length > 0),
      }));
      const res = await schoolFetch('/school/subject-choices/enrolments', {
        method: 'PUT',
        body: JSON.stringify({ ...shownContext(), enrolments }),
      });
      if (!handleResult(res, 'Could not save student choices.')) return;
      await load();
      setSuccess(`${res.data.message}${syncNote(res.data.sync)}`);
    } catch {
      setError('Could not save student choices. Check your connection and try again.');
    } finally { setBusy(''); }
  };

  const students = data?.students || [];
  const studentRows = students.filter(st => {
    const map = choices[st.membershipId] || {};
    if (onlyIncomplete && isComplete(savedGroups, map)) return false;
    if (!search.trim()) return true;
    const hay = `${st.firstName} ${st.lastName} ${st.studentId} ${st.classArm}`.toLowerCase();
    return hay.includes(search.trim().toLowerCase());
  });
  const completeCount = students.filter(st => isComplete(savedGroups, choices[st.membershipId] || {})).length;
  const incompleteCount = students.length - completeCount;
  const canEditChoices = !!policy && savedGroups.length > 0 && !groupsChanged;

  const statusChip = !policy
    ? <span className={`${css.chip} ${css.chipPending}`}>Not set up</span>
    : isActive
      ? <span className={`${css.chip} ${css.chipActive}`}>On</span>
      : <span className={`${css.chip} ${css.chipInactive}`}>Off (draft)</span>;

  return (
    <div className={css.card}>
      <div className={css.cardHeader}>
        <h2 className={css.cardTitle} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AltRouteIcon style={{ fontSize: '1.2rem', color: '#3e8f93' }} /> Subject Choices (Electives)
        </h2>
        {data && statusChip}
      </div>
      <p style={{ ...subtle, marginTop: -8, marginBottom: 14 }}>
        Use this when students in the same class take different subjects — for example <strong>CRS or IRS</strong>,
        or <strong>Government instead of Physics</strong>. Subjects you don't put in a group stay compulsory for everyone.
        Classes without subject choices turned on keep working exactly as before.
      </p>

      {classes.length === 0 ? (
        <div className={css.emptyState}>
          <p className={css.emptyText}>Add a class first</p>
          <p className={css.emptySub}>Subject choices are set up per class once it has subjects assigned.</p>
        </div>
      ) : (
        <>
          <div style={filterRow}>
            <div className={css.formGroup} style={filterField}>
              <label className={css.formLabel} htmlFor="sc-class">Class</label>
              <select id="sc-class" className={css.formSelect} value={classId} onChange={e => changeClass(e.target.value)} disabled={Boolean(busy)}>
                {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div className={css.formGroup} style={filterField}>
              <label className={css.formLabel} htmlFor="sc-term">Term</label>
              <select id="sc-term" className={css.formSelect} value={term} onChange={e => changeTerm(e.target.value)} disabled={loading || Boolean(busy)}>
                {!term && <option value="">Select term</option>}
                {TERM_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className={css.formGroup} style={filterField}>
              <label className={css.formLabel} htmlFor="sc-session">Academic session</label>
              <input
                id="sc-session"
                className={css.formInput}
                value={sessionDraft}
                placeholder="e.g. 2025/2026"
                onChange={e => setSessionDraft(e.target.value)}
                onBlur={applySession}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applySession(); } }}
                disabled={loading || Boolean(busy)}
              />
            </div>
          </div>
          {data && (data.term !== data.currentTerm || data.session !== data.currentSession) && data.currentSession && (
            <div className={css.formHint} style={{ marginTop: -8, marginBottom: 12 }}>
              You're viewing a different term from the school's current term ({TERM_OPTIONS.find(t => t.value === data.currentTerm)?.label} {data.currentSession}).
              Report cards and exams for that term follow the choices saved there.
            </div>
          )}

          {error && <div className={css.inlineError} role="alert">{error}</div>}
          {success && <div className={css.inlineSuccess} role="status">{success}</div>}

          {loading && <p style={subtle}>Loading subject choices…</p>}

          {!loading && data && (
            <>
              <div style={stepBox}>
                <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#0C1E28', marginBottom: 6 }}>How it works</div>
                <ol style={{ ...subtle, margin: 0, paddingLeft: 18 }}>
                  <li><strong>Create a group</strong> of subjects students choose between (e.g. "Religious Studies": CRS, IRS) and say how many each student takes.</li>
                  <li><strong>Record each student's choice</strong> in the table below.</li>
                  <li><strong>Turn subject choices on.</strong> New elective exams then go only to students who chose that subject, attendance registers list only those students, and report cards show only the subjects each student takes. Positions are still out of the whole class.</li>
                </ol>
              </div>

              {classSubjects.length < 2 ? (
                <div className={css.emptyState}>
                  <p className={css.emptyText}>This class needs at least two subjects</p>
                  <p className={css.emptySub}>Assign subjects to {data.classInfo?.name} in the Subjects list above, then come back here.</p>
                </div>
              ) : (
                <>
                  {/* ── Step 1: groups ── */}
                  <div style={sectionTitle}>1. Elective groups for {data.classInfo?.name}</div>
                  <p style={{ ...subtle, marginTop: 0 }}>Tick the subjects students choose between. A subject can only be in one group.</p>

                  {!policy && (data.copySources || []).length > 0 && (
                    <div style={{ ...stepBox, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
                      <div className={css.formGroup} style={{ ...filterField, flex: '2 1 220px' }}>
                        <label className={css.formLabel} htmlFor="sc-copy">Start from an earlier term (optional)</label>
                        <select id="sc-copy" className={css.formSelect} value={copySource} onChange={e => setCopySource(e.target.value)}>
                          <option value="">Choose a term to copy from</option>
                          {data.copySources.map((src, i) => <option key={`${src.term}-${src.session}`} value={i}>{src.label}</option>)}
                        </select>
                      </div>
                      <button type="button" className={css.btnSecondary} onClick={copyFrom} disabled={copySource === '' || busy === 'copy'}>
                        <ContentCopyIcon style={{ fontSize: '0.95rem' }} /> {busy === 'copy' ? 'Copying…' : 'Copy groups & choices'}
                      </button>
                    </div>
                  )}

                  {groups.length === 0 && (
                    <p style={{ ...subtle, fontStyle: 'italic' }}>No groups yet. Press "Add elective group" to create one.</p>
                  )}

                  {groups.map((g, index) => {
                    const used = usedElsewhere(g.key);
                    const maxChoose = Math.max(1, g.subjectIds.length - 1);
                    return (
                      <div key={g.key} style={groupBox}>
                        <div className={css.formRow}>
                          <div className={css.formGroup}>
                            <label className={css.formLabel} htmlFor={`sc-name-${g.key}`}>Group name</label>
                            <input id={`sc-name-${g.key}`} className={css.formInput} value={g.name} maxLength={80}
                              placeholder={index === 0 ? 'e.g. Religious Studies' : 'e.g. Science or Commercial'}
                              onChange={e => updateGroup(g.key, { name: e.target.value })} />
                          </div>
                          <div className={css.formGroup}>
                            <label className={css.formLabel} htmlFor={`sc-choose-${g.key}`}>Each student takes</label>
                            <select id={`sc-choose-${g.key}`} className={css.formSelect} value={g.choose}
                              onChange={e => updateGroup(g.key, { choose: Number(e.target.value) })}
                              disabled={g.subjectIds.length < 2}>
                              {Array.from({ length: maxChoose }, (_, i) => i + 1).map(n => (
                                <option key={n} value={n}>{n} subject{n === 1 ? '' : 's'}{g.subjectIds.length >= 2 ? ` of ${g.subjectIds.length}` : ''}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className={css.formGroup}>
                          <label className={css.formLabel} htmlFor={`sc-desc-${g.key}`}>Note for staff (optional)</label>
                          <input id={`sc-desc-${g.key}`} className={css.formInput} value={g.description} maxLength={300}
                            placeholder="e.g. Based on the student's faith"
                            onChange={e => updateGroup(g.key, { description: e.target.value })} />
                        </div>
                        <div className={css.formGroup} style={{ marginBottom: 8 }}>
                          <span className={css.formLabel}>Subjects in this group</span>
                          <div className={css.checkGrid}>
                            {classSubjects.map(s => {
                              const elsewhere = used.get(s._id);
                              return (
                                <label key={s._id} className={css.checkLabel} title={elsewhere ? `Already in ${elsewhere}` : undefined} style={elsewhere ? { opacity: 0.55 } : undefined}>
                                  <input className="schoolCheckbox" type="checkbox" checked={g.subjectIds.includes(s._id)}
                                    disabled={!!elsewhere} onChange={() => toggleGroupSubject(g.key, s._id)} />
                                  {s.name}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                        <div style={{ ...actionRow, justifyContent: 'space-between' }}>
                          <span className={css.cellSub}>
                            {g.subjectIds.length >= 2
                              ? `Students pick ${g.choose} of: ${g.subjectIds.map(id => subjectName[id]).filter(Boolean).join(', ')}`
                              : 'Tick at least two subjects.'}
                          </span>
                          <button type="button" className={css.btnDanger} style={smallBtn}
                            onClick={() => setGroups(prev => prev.filter(x => x.key !== g.key))}
                            disabled={isActive && groups.length === 1}
                            title={isActive && groups.length === 1 ? 'Turn subject choices off before removing the last group' : 'Remove group'}>
                            <DeleteIcon style={{ fontSize: '0.9rem' }} /> Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  <div style={actionRow}>
                    <button type="button" className={css.btnSecondary} onClick={() => setGroups(prev => [...prev, newGroup()])} disabled={groups.length >= MAX_GROUPS}>
                      <AddIcon style={{ fontSize: '1rem' }} /> Add elective group
                    </button>
                    <button type="button" className={css.btnPrimary} onClick={saveGroups} disabled={!groupsChanged || busy === 'groups'}>
                      {busy === 'groups' ? 'Saving…' : 'Save groups'}
                    </button>
                    {groupsChanged && <span className={css.cellSub}>You have unsaved group changes.</span>}
                  </div>
                  {groupsChanged && groupErrors.length > 0 && groups.length > 0 && (
                    <div className={css.formHint} style={{ color: '#c62828' }}>{groupErrors[0]}</div>
                  )}
                  {isActive && groupsChanged && (
                    <div className={css.formHint}>Subject choices are on, so saving updates this class straight away. Choices that no longer fit a group are cleared for those students.</div>
                  )}

                  {/* ── Step 2: student choices ── */}
                  <div style={sectionTitle}>2. Each student's choices</div>
                  {!canEditChoices ? (
                    <p style={{ ...subtle, marginTop: 0 }}>
                      {groupsChanged && policy ? 'Save your group changes first, then record students\' choices here.' : 'Save at least one elective group to start recording choices.'}
                    </p>
                  ) : students.length === 0 ? (
                    <div className={css.emptyState}>
                      <p className={css.emptyText}>No active students in {data.classInfo?.name}</p>
                      <p className={css.emptySub}>Students added to this class will appear here.</p>
                    </div>
                  ) : (
                    <>
                      <p style={{ ...subtle, marginTop: 0 }}>
                        <strong>{completeCount}</strong> of {students.length} student(s) have all their choices.
                        {incompleteCount > 0 && <> <strong style={{ color: '#f57f17' }}>{incompleteCount}</strong> still need choices — until then, only their compulsory subjects (and any electives already chosen) appear on their report card.</>}
                      </p>

                      {savedGroups.some(g => g.choose === 1) && (
                        <div style={{ ...filterRow, marginBottom: 10 }}>
                          {savedGroups.filter(g => g.choose === 1).map(g => (
                            <div key={g._id} className={css.formGroup} style={filterField}>
                              <label className={css.formLabel} htmlFor={`sc-fill-${g._id}`}>Quick fill {g.name} (students without a choice)</label>
                              <select id={`sc-fill-${g._id}`} className={css.formSelect} value="" onChange={e => fillBlanks(g, e.target.value)}>
                                <option value="">Choose a subject…</option>
                                {g.subjectIds.map(id => <option key={id} value={id}>{subjectName[id]}</option>)}
                              </select>
                            </div>
                          ))}
                        </div>
                      )}

                      <div style={{ ...filterRow, marginBottom: 10 }}>
                        <input className={css.formInput} style={{ flex: '2 1 200px' }} placeholder="Search by name, ID or arm…" value={search} onChange={e => setSearch(e.target.value)} aria-label="Search students" />
                        <label className={css.checkLabel} style={{ flex: '1 1 180px' }}>
                          <input className="schoolCheckbox" type="checkbox" checked={onlyIncomplete} onChange={e => setOnlyIncomplete(e.target.checked)} />
                          Only show students who still need choices
                        </label>
                      </div>

                      <div className={css.tableWrap}>
                        <table className={css.table}>
                          <thead>
                            <tr>
                              <th>Student</th>
                              {savedGroups.map(g => <th key={g._id}>{g.name}<div className={css.cellSub} style={{ textTransform: 'none', fontWeight: 400 }}>Pick {g.choose}</div></th>)}
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {studentRows.length === 0 ? (
                              <tr><td colSpan={savedGroups.length + 2} style={{ textAlign: 'center', color: '#90a4ae' }}>No students match.</td></tr>
                            ) : studentRows.map(st => {
                              const map = choices[st.membershipId] || {};
                              const done = isComplete(savedGroups, map);
                              return (
                                <tr key={st.membershipId}>
                                  <td>
                                    <strong>{`${st.firstName} ${st.lastName}`.trim() || 'Student'}</strong>
                                    <div className={css.cellSub}>{[st.studentId, st.classArm && `Arm ${st.classArm}`].filter(Boolean).join(' · ')}</div>
                                  </td>
                                  {savedGroups.map(g => {
                                    const picked = map[g._id] || [];
                                    return (
                                      <td key={g._id}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 120 }}>
                                          {g.subjectIds.map(id => {
                                            const checked = picked.includes(id);
                                            const full = g.choose > 1 && !checked && picked.length >= g.choose;
                                            return (
                                              <label key={id} className={css.checkLabel} style={full ? { opacity: 0.55 } : undefined}>
                                                {g.choose === 1 ? (
                                                  <input className="schoolRadio" type="radio" name={`sc-${st.membershipId}-${g._id}`} checked={checked}
                                                    onChange={() => setStudentChoice(st.membershipId, g, id, true)} />
                                                ) : (
                                                  <input className="schoolCheckbox" type="checkbox" checked={checked} disabled={full}
                                                    onChange={e => setStudentChoice(st.membershipId, g, id, e.target.checked)} />
                                                )}
                                                {subjectName[id] || 'Subject'}
                                              </label>
                                            );
                                          })}
                                          {g.choose === 1 && picked.length > 0 && (
                                            <button type="button" className={css.btnSecondary} style={{ ...smallBtn, alignSelf: 'flex-start', padding: '3px 8px' }}
                                              onClick={() => setStudentChoice(st.membershipId, g, picked[0], false)}>
                                              Clear
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    );
                                  })}
                                  <td>
                                    {done
                                      ? <span className={`${css.chip} ${css.chipActive}`}>Complete</span>
                                      : <span className={`${css.chip} ${css.chipInactive}`}>Needs choice</span>}
                                    {dirty.has(st.membershipId) && <div className={css.cellSub}>Unsaved</div>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <div style={{ ...actionRow, marginTop: 12 }}>
                        <button type="button" className={css.btnPrimary} onClick={saveChoices} disabled={!hasUnsavedChoices || busy === 'choices'}>
                          {busy === 'choices' ? 'Saving…' : `Save student choices${hasUnsavedChoices ? ` (${dirty.size})` : ''}`}
                        </button>
                        {hasUnsavedChoices && (
                          <button type="button" className={css.btnSecondary} onClick={() => load()} disabled={busy === 'choices'}>Discard changes</button>
                        )}
                      </div>
                    </>
                  )}

                  {/* ── Step 3: on/off ── */}
                  {policy && savedGroups.length > 0 && (
                    <>
                      <div style={sectionTitle}>3. Turn subject choices {isActive ? 'off' : 'on'}</div>
                      <p style={{ ...subtle, marginTop: 0 }}>
                        {isActive
                          ? `Subject choices are ON for ${data.classInfo?.name} this term. Turning them off makes every class subject apply to every student again — saved choices are kept so you can turn them back on.`
                          : `Subject choices are OFF, so every class subject still applies to every student in ${data.classInfo?.name}. Turn them on once students' choices are recorded.`}
                      </p>
                      <div style={actionRow}>
                        {isActive ? (
                          <button type="button" className={css.btnDanger} onClick={() => setConfirm('draft')} disabled={busy === 'status' || groupsChanged}>
                            Turn subject choices off
                          </button>
                        ) : (
                          <button type="button" className={css.btnPrimary} onClick={() => setConfirm('active')} disabled={busy === 'status' || groupsChanged}>
                            <CheckCircleIcon style={{ fontSize: '1rem' }} /> Turn subject choices on
                          </button>
                        )}
                        {groupsChanged && <span className={css.cellSub}>Save your group changes first.</span>}
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      {confirm && (
        <div className={css.modalOverlay} onClick={() => setConfirm(null)}>
          <div className={css.modal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="sc-confirm-title">
            <h3 id="sc-confirm-title" className={css.modalTitle}>
              {confirm === 'active' ? 'Turn subject choices on?' : 'Turn subject choices off?'}
            </h3>
            {confirm === 'active' ? (
              <div style={subtle}>
                <p style={{ marginTop: 0 }}>For {data?.classInfo?.name}, {TERM_OPTIONS.find(t => t.value === term)?.label} {session}:</p>
                <ul style={{ paddingLeft: 18 }}>
                  <li>New exams for an elective subject go only to students who chose it.</li>
                  <li>Open elective exams already given to the class are added to or removed from students' lists to match their choices (exams a student has started or finished are never removed).</li>
                  <li>Report cards, mid-term reports and broadsheets show only the subjects each student takes (others show N/A). Positions are still out of the whole class.</li>
                  <li>Subject attendance registers list only students taking that subject.</li>
                </ul>
                {incompleteCount > 0 && (
                  <div className={css.inlineError} style={{ marginBottom: 0 }}>
                    {incompleteCount} student(s) still need choices. They'll only see compulsory subjects (and electives already chosen) until you record their choices.
                  </div>
                )}
              </div>
            ) : (
              <p style={subtle}>Every class subject will apply to every student in {data?.classInfo?.name} again for this term. Students' saved choices are kept, and no exam results are deleted.</p>
            )}
            <div className={css.modalFooter} style={{ padding: '16px 0 0', flexWrap: 'wrap' }}>
              <button type="button" className={css.btnSecondary} onClick={() => setConfirm(null)}>Cancel</button>
              <button type="button" className={confirm === 'active' ? css.btnPrimary : css.btnDanger} onClick={() => setStatus(confirm)}>
                {confirm === 'active' ? 'Turn on' : 'Turn off'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
